<script setup lang="ts">
import { ApiClientError } from "@shopware/api-client";
import type { ApiError } from "@shopware/api-client";
import { useTemplateRef } from "vue";

const emits = defineEmits<{
  (e: "success"): void;
  (e: "close"): void;
}>();
const router = useRouter();
const { isLoggedIn, login } = useUser();
const { t } = useI18n();
const { refreshSessionContext } = useSessionContext();
const { mergeWishlistProducts } = useWishlist();
const { pushSuccess } = useNotifications();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const loginErrors = ref<string[]>([]);
const loading = ref<boolean>(true);

const formData = ref({
  username: "",
  password: "",
  remember: true,
});

const goToRegister = () => {
  emits("close");
  router.push(formatLink("/register"));
};

const { resolveApiErrors } = useApiErrorsResolver("account_login");

const invokeLogin = async (): Promise<void> => {
  loginErrors.value = [];
  try {
    await refreshSessionContext();
    loading.value = true;
    await login(formData.value);
    emits("success");
    pushSuccess(t("account.messages.loggedInSuccess"));
    emits("close");
    mergeWishlistProducts();
    loading.value = false;
  } catch (error) {
    loading.value = false;
    // if (error instanceof ApiClientError) {
      loginErrors.value = resolveApiErrors(error.details.errors as ApiError[]);
    // }
  }
};

const emailInputElement = useTemplateRef("emailInputElement");
useFocus(emailInputElement, { initialValue: true });

onMounted(() => {
  loading.value = false;
});
</script>
<template>
  <div
    id="modal-headline"
    role="form"
    title="Login form"
    :aria-label="$t('loginForm.loginFormAriaLabel')"
    class="min-h-full flex justify-center py-12 px-4 sm:px-6 lg:px-8"
  >
    <div v-if="!isLoggedIn" class="max-w-full xl:max-w-md w-full space-y-8 mx-auto">
      <div class="login-form block xl:sticky top-10 pt-0 md:pt-16 lg:pt-0">
        <h2 class="font-marcellus text-2xl md:text-3xl 3xl:text-4xl text-gray-700 mb-4 leading-[30px]">
          {{ $t("account.login.loginFormTitle") }}
        </h2>
        <form class="flex flex-col gap-6 mt-6 lg:mt-10" @submit.prevent="invokeLogin">
          <p class="text-gray-700 leading-[1.3] md:leading-[1.1]">
            {{ $t("account.login.loginFormDescription") }}
          </p>
          <label for="login-email-address" class="sr-only">{{ $t('account.login.loginFormUsername') }}</label>
          <input
            v-model="formData.username"
            id="login-email-address"
            ref="emailInputElement"
            name="email"
            type="email"
            autocomplete="email"
            required
            :placeholder="$t('account.login.loginFormUsername')"
            class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base border-secondary-300 focus:border-primary"
            data-testid="login-email-input"
          />
          <label for="password" class="sr-only">{{ $t('account.login.loginFormPassword') }}</label>
          <input
            v-model="formData.password"
            id="password"
            name="password"
            type="password"
            autocomplete="current-password"
            required
            :placeholder="$t('account.login.loginFormPassword')"
            class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base border-secondary-300 focus:border-primary"
            data-testid="login-password-input"
          />
          <slot name="error">
            <div
              v-if="loginErrors.length"
              class="flex items-center justify-between"
              data-testid="login-errors-container"
            >
              <div class="flex items-center">
                <div v-for="error in loginErrors" class="login-errors text-red focus:ring-primary border-secondary-300 rounded">
                  {{ error }}
                </div>
              </div>
            </div>
          </slot>
          <NuxtLink :to="formatLink(`/account/recover`)" class="text-gray-900 underline font-medium transition-colors leading-[1]">
            {{ $t("account.login.loginFormForgotPassword") }}
          </NuxtLink>
          <slot :data="formData" />
          
          <button
            type="submit"
            class="btn btn-primary btn-full sm:w-fit md:px-8 text-center mt-4"
            data-testid="login-submit-button"
            :disabled="loading"
          >
            <span class="my-auto">
              {{ $t("account.signIn") }}
            </span>
          </button>
          <!-- Benefits Section -->
          <div class="benefits-section mt-8 mb-12 md:mt-8 md:mb-0">
            <h3 class="text-xl md:text-2xl text-gray-700 mb-4">
              {{ $t("account.login.loginFormBenefits") }}
            </h3>
            <ul class="space-y-1 list-disc list-outside text-gray-700 pl-0 md:pl-2">
              <li>
                {{ $t("account.login.loginFormBenefitsItem1") }}
              </li>
              <li>
                {{ $t("account.login.loginFormBenefitsItem2") }}
              </li>
              <li>
                {{ $t("account.login.loginFormBenefitsItem3") }}
              </li>
              <li>
                {{ $t("account.login.loginFormBenefitsItem4") }}
              </li>
            </ul>
          </div>
        </form>
      </div>
    </div>
    <div v-else>
      <h2>{{ $t("account.loggedInInfo") }}</h2>
    </div>
  </div>
</template>
